import React, { useState, useEffect } from 'react';
import LandingPage from './LandingPage';
import DemoPage from './DemoPage';
import OutfitLandingPage from './OutfitLandingPage';
import OutfitDemoPage from './OutfitDemoPage';
import { Dashboard } from './dashboard';
import ResetPasswordPage from './components/ResetPasswordPage';
import AuthPage from './components/AuthPage';
import TermsOfUsePage from './components/TermsOfUsePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import ApiDocsPage from './components/ApiDocsPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import CookieConsentBanner from './components/CookieConsentBanner';

const App = () => {
  const [currentPage, setCurrentPage] = useState('landing');

  useEffect(() => {
    // Check URL on load
    const path = window.location.pathname;
    if (path === '/demo') {
      setCurrentPage('demo');
    } else if (path === '/outfit/demo') {
      setCurrentPage('outfit-demo');
    } else if (path === '/outfit') {
      setCurrentPage('outfit');
    } else if (path === '/dashboard' || path.startsWith('/dashboard/')) {
      setCurrentPage('dashboard');
    } else if (path === '/reset-password') {
      setCurrentPage('reset-password');
    } else if (path === '/auth') {
      setCurrentPage('auth');
    } else if (path === '/terms') {
      setCurrentPage('terms');
    } else if (path === '/privacy') {
      setCurrentPage('privacy');
    } else if (path === '/api-docs') {
      setCurrentPage('api-docs');
    } else if (path === '/blog' || path === '/blog/') {
      setCurrentPage('blog');
    } else if (path.startsWith('/blog/')) {
      setCurrentPage('blog-post');
    } else {
      setCurrentPage('landing');
    }

    // Listen for popstate (back/forward buttons)
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/demo') {
        setCurrentPage('demo');
      } else if (path === '/outfit/demo') {
        setCurrentPage('outfit-demo');
      } else if (path === '/outfit') {
        setCurrentPage('outfit');
      } else if (path === '/dashboard' || path.startsWith('/dashboard/')) {
        setCurrentPage('dashboard');
      } else if (path === '/reset-password') {
        setCurrentPage('reset-password');
      } else if (path === '/auth') {
        setCurrentPage('auth');
      } else if (path === '/terms') {
        setCurrentPage('terms');
      } else if (path === '/privacy') {
        setCurrentPage('privacy');
      } else if (path === '/api-docs') {
        setCurrentPage('api-docs');
      } else if (path === '/blog' || path === '/blog/') {
        setCurrentPage('blog');
      } else if (path.startsWith('/blog/')) {
        setCurrentPage('blog-post');
      } else {
        setCurrentPage('landing');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateTo = (page, opts = {}) => {
    const hash = opts.hash ? `#${opts.hash}` : '';
    if (page === 'demo') {
      window.history.pushState({}, '', '/demo');
      setCurrentPage('demo');
    } else if (page === 'outfit') {
      window.history.pushState({}, '', '/outfit');
      setCurrentPage('outfit');
    } else if (page === 'outfit-demo') {
      window.history.pushState({}, '', '/outfit/demo');
      setCurrentPage('outfit-demo');
    } else if (page === 'dashboard') {
      window.history.pushState({}, '', '/dashboard');
      setCurrentPage('dashboard');
    } else if (page === 'terms') {
      window.history.pushState({}, '', '/terms');
      setCurrentPage('terms');
    } else if (page === 'privacy') {
      window.history.pushState({}, '', `/privacy${hash}`);
      setCurrentPage('privacy');
    } else if (page === 'api-docs') {
      window.history.pushState({}, '', '/api-docs');
      setCurrentPage('api-docs');
    } else if (page === 'blog') {
      window.history.pushState({}, '', '/blog');
      setCurrentPage('blog');
    } else if (page === 'blog-post' && opts.slug) {
      window.history.pushState({}, '', `/blog/${opts.slug}`);
      setCurrentPage('blog-post');
    } else {
      window.history.pushState({}, '', '/');
      setCurrentPage('landing');
    }
  };

  let page;
  if (currentPage === 'demo') {
    page = <DemoPage onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'outfit') {
    page = (
      <OutfitLandingPage 
        onNavigateToDemo={() => navigateTo('outfit-demo')} 
        onNavigateToEyewear={() => navigateTo('landing')}
        onNavigateToTerms={() => navigateTo('terms')}
        onNavigateToPrivacy={() => navigateTo('privacy')}
        onNavigateToCookiePolicy={() => navigateTo('privacy', { hash: 'cookies' })}
        onNavigateToApiDocs={() => navigateTo('api-docs')}
      />
    );
  } else if (currentPage === 'outfit-demo') {
    page = <OutfitDemoPage onBack={() => navigateTo('outfit')} />;
  } else if (currentPage === 'dashboard') {
    page = <Dashboard onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'reset-password') {
    page = <ResetPasswordPage onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'auth') {
    page = <AuthPage onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'terms') {
    page = <TermsOfUsePage onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'privacy') {
    page = <PrivacyPolicyPage onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'api-docs') {
    page = <ApiDocsPage onBack={() => navigateTo('landing')} />;
  } else if (currentPage === 'blog') {
    page = (
      <BlogPage
        onBack={() => navigateTo('landing')}
        onOpenPost={(slug) => navigateTo('blog-post', { slug })}
        onNavigateToDemo={() => navigateTo('demo')}
        onNavigateToOutfit={() => navigateTo('outfit')}
        onNavigateToBlog={() => navigateTo('blog')}
        onNavigateToDashboard={() => navigateTo('dashboard')}
        onNavigateToTerms={() => navigateTo('terms')}
        onNavigateToPrivacy={() => navigateTo('privacy')}
        onNavigateToCookiePolicy={() => navigateTo('privacy', { hash: 'cookies' })}
        onNavigateToApiDocs={() => navigateTo('api-docs')}
      />
    );
  } else if (currentPage === 'blog-post') {
    const slug = (typeof window !== 'undefined' && window.location.pathname.replace(/^\/blog\/?/, '')) || '';
    page = (
      <BlogPostPage
        slug={slug}
        onBack={() => navigateTo('blog')}
        onNavigateToDemo={() => navigateTo('demo')}
        onNavigateToOutfit={() => navigateTo('outfit')}
        onNavigateToBlog={() => navigateTo('blog')}
        onNavigateToDashboard={() => navigateTo('dashboard')}
        onNavigateToTerms={() => navigateTo('terms')}
        onNavigateToPrivacy={() => navigateTo('privacy')}
        onNavigateToCookiePolicy={() => navigateTo('privacy', { hash: 'cookies' })}
        onNavigateToApiDocs={() => navigateTo('api-docs')}
      />
    );
  } else {
    page = (
      <LandingPage 
        onNavigateToDemo={() => navigateTo('demo')} 
        onNavigateToOutfit={() => navigateTo('outfit')}
        onNavigateToBlog={() => navigateTo('blog')}
        onNavigateToDashboard={() => navigateTo('dashboard')}
        onNavigateToTerms={() => navigateTo('terms')}
        onNavigateToPrivacy={() => navigateTo('privacy')}
        onNavigateToCookiePolicy={() => navigateTo('privacy', { hash: 'cookies' })}
        onNavigateToApiDocs={() => navigateTo('api-docs')}
      />
    );
  }

  const showCookieBanner =
    currentPage === 'landing' &&
    (typeof window === 'undefined' || window.location.pathname === '/');

  return (
    <>
      {page}
      {showCookieBanner ? <CookieConsentBanner /> : null}
    </>
  );
};

export default App;

